import AddIcon from '@diligentcorp/atlas-react-icons/dist/esm/lens/Add.js';
import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
  useTheme,
} from '@mui/material';
import { useState } from 'react';
import {
  MitigationPlanFragment,
  MitigationPlanStatus,
  useMitigationPlansForDisclosureQuery,
} from 'src/graphql';
import { AddMitigationPlanDrawer } from './AddMitigationPlanDrawer';
import { MitigationPlanCard } from './MitigationPlanCard';

interface Props {
  caseId: string;
  userMap: Record<number, { name: string; role?: string }>;
  canAdd?: boolean;
  onAddClick?: () => void;
  onEditClick?: (plan: MitigationPlanFragment) => void;
  onPlanRecalled?: () => void;
  onPlanUpdated?: () => void;
  caseStatus?: string;
}

export const MitigationPlanSection = ({
  caseId,
  userMap,
  canAdd = false,
  onAddClick,
  onEditClick,
  onPlanRecalled,
  onPlanUpdated,
  caseStatus,
}: Props) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;
  const sp = tokens.semantic.spacing.desktop.gap;

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editPlan, setEditPlan] = useState<MitigationPlanFragment | null>(null);

  const { data, isLoading, isError, refetch } =
    useMitigationPlansForDisclosureQuery({ caseId });

  const plans =
    data?.mitigationPlansForDisclosure.filter(
      (plan) => plan.status !== MitigationPlanStatus.NoApprovalRequired,
    ) ?? [];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: sp[2].value }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography
            variant="h3"
            fontWeight={tokens.semantic.fontWeight.emphasis.value}
            color={color.type.default.value}
            pb={sp[1].value}
          >
            Mitigation plans
          </Typography>
          <Typography variant="body1" color={color.type.muted.value}>
            You can assign mitigation plans to this report
          </Typography>
        </Box>
        {canAdd && (
          <Button
            variant="outlined"
            size="small"
            onClick={onAddClick ?? (() => setDrawerOpen(true))}
            startIcon={<AddIcon size="md" />}
            data-testid="add-mitigation-plan-button"
            disabled={caseStatus === 'CLOSED'}
            sx={{
              whiteSpace: 'nowrap',
              textTransform: 'none',
              borderRadius: tokens.semantic.radius.lg.value,
              borderColor: color.type.default.value,
              color: color.type.default.value,
              '&:hover': {
                borderColor: color.type.default.value,
                backgroundColor: 'transparent',
              },
            }}
          >
            Add mitigation plan
          </Button>
        )}
      </Box>

      {isLoading ? (
        <Box
          sx={{ display: 'flex', justifyContent: 'center', py: sp[3].value }}
        >
          <CircularProgress size={24} />
        </Box>
      ) : isError ? (
        <Alert severity="error">
          Failed to load mitigation plans. Please try again.
        </Alert>
      ) : plans.length === 0 ? (
        <EmptyState color={color} sp={sp} />
      ) : (
        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: sp[1].value }}
        >
          {plans.map((plan) => (
            <MitigationPlanCard
              key={plan.id}
              plan={plan}
              userMap={userMap}
              canEdit={canAdd}
              onRecalled={() => {
                refetch();
                onPlanRecalled?.();
              }}
              onEditClick={(p) => {
                if (onEditClick) {
                  onEditClick(p);
                } else {
                  setEditPlan(p);
                  setDrawerOpen(true);
                }
              }}
              onPlanUpdated={() => {
                refetch();
                onPlanUpdated?.();
              }}
            />
          ))}
        </Box>
      )}

      {(canAdd && !onAddClick && !onEditClick) || editPlan ? (
        <AddMitigationPlanDrawer
          open={drawerOpen}
          caseId={caseId}
          editPlan={editPlan}
          onClose={() => {
            setDrawerOpen(false);
            setEditPlan(null);
          }}
          onSuccess={() => {
            setEditPlan(null);
            refetch();
          }}
        />
      ) : null}
    </Box>
  );
};

interface EmptyStateProps {
  color: LensTheme['tokens']['semantic']['color'];
  sp: LensTheme['tokens']['semantic']['spacing']['desktop']['gap'];
}

const EmptyState = ({ color, sp }: EmptyStateProps) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: sp[4].value,
      gap: sp[1].value,
    }}
    data-testid="mitigation-plans-empty-state"
  >
    <Typography variant="body2" color={color.type.muted.value}>
      No mitigation plans
    </Typography>
  </Box>
);
